import axios from "axios";

import { SIGN_IN_URL, SIGN_OUT_URL } from "../constants/api-constants";
import {
  cleanUserTokensFromLocalStorage,
  getAccessToken,
  getRefreshToken,
  setUserTokensToLocalStorage,
} from "./helpers/auth";
import http from "./http-common";

interface Props {
  username: string;
  password: string;
  business_network?: string;
  system_admin?: boolean;
}

interface ErrorProps {
  status?: number;
  response?: { status: number };
}

const authProvider = {
  login: async ({ username, password, business_network, system_admin }: Props) => {
    cleanUserTokensFromLocalStorage();
    const request = await axios.post(`${process.env.REACT_APP_API_URL}${SIGN_IN_URL}`,
      { username, password, business_network, system_admin });
    if (!request.data.accessToken || !request.data.refreshToken) throw new Error('Login failed');
    const identity = await axios.post(`${process.env.REACT_APP_API_URL}/auth/me`, { token: request.data.refreshToken });
    if (!['ADMIN', 'SYSTEM_ADMIN'].includes(identity.data.role)) throw new Error('Administrator access required');
    setUserTokensToLocalStorage(request.data.accessToken, request.data.refreshToken);
  },
  logout: async () => {
    try {
      if (getAccessToken()) await http.post(SIGN_OUT_URL, { refreshToken: getRefreshToken() });
    } finally { cleanUserTokensFromLocalStorage(); }
  },
  checkAuth: () => (getAccessToken() ? Promise.resolve() : Promise.reject()),
  checkError: (error: ErrorProps) => {
    const status = error.response?.status || error.status;
    if (status === 401 || status === 403) {
      cleanUserTokensFromLocalStorage();
      return Promise.reject();
    }
    return Promise.resolve();
  },
  getIdentity: async () => {
    try {
      const token = getRefreshToken();
      if (!token) throw new Error('Missing token');
      const request = await axios.post(`${process.env.REACT_APP_API_URL}/auth/me`, { token });

      const user = request.data;
      return Promise.resolve({
        id: user.id,
        fullName: user.first_name,
      });
    } catch (error) {
      await authProvider.logout();
      return Promise.reject("Failed to fetch user. Logging out...");
    }
  },
  getPermissions: async () => {
    if (!getAccessToken()) return { role: null, network: null, modules: [] };
    const response = await http.get('/auth/info');
    const user = response.data;
    if (!['ADMIN', 'SYSTEM_ADMIN'].includes(user.role?.value)) throw new Error('Administrator access required');
    const modules = user.role.value === 'ADMIN' ?
      (await http.get(`/admin/network-modules/${user.business_network.id}`)).data : [];
    return { role: user.role.value, network: user.business_network, modules };
  }
};

export default authProvider;
